import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./NookArcher.scss";

const ARCHER_SRC = "/images/nook-archer.png";

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// Cropped sprite is 495×779. Tip of the nocked arrow, hips for the walk.
const ARCHER = { w: 104, h: 164 };
const ARCHER_MOBILE = { w: 78, h: 123 };
const TIP = { x: 0.115, y: 0.34 };

function measurePose(stageEl, boardEl, wickEl) {
  const mobile =
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 640px)").matches;
  const { w, h } = mobile ? ARCHER_MOBILE : ARCHER;

  if (!stageEl || !wickEl) {
    return {
      w,
      h,
      walkY: 1680,
      startX: 720,
      stopX: 96,
      boltX: 108,
      boltY: 1724,
      wickX: 28,
      wickY: 1710,
      flyDeg: -8,
    };
  }

  const stage = stageEl.getBoundingClientRect();
  const wick = wickEl.getBoundingClientRect();
  const board = boardEl ? boardEl.getBoundingClientRect() : null;

  const wickX = wick.left + wick.width / 2 - stage.left;
  const wickY = wick.top + wick.height / 2 - stage.top;
  const boardTop = board ? board.top - stage.top : wickY + 28;
  // Plant the bulky boots a few pixels into the shelf board.
  const walkY = Math.round(boardTop - h + 6);
  const startX = Math.round(stage.width + 12);
  const stopX = Math.round(
    Math.min(startX - 56, Math.max(wickX + 70, 40))
  );

  const boltX = stopX + w * TIP.x;
  const boltY = walkY + h * TIP.y;
  const dx = wickX - boltX;
  const dy = wickY - boltY;
  const flyDeg = Math.round(-180 - (Math.atan2(dy, dx) * 180) / Math.PI);

  return {
    w,
    h,
    walkY,
    startX,
    stopX,
    boltX: Math.round(boltX),
    boltY: Math.round(boltY),
    wickX: Math.round(wickX),
    wickY: Math.round(wickY),
    flyDeg,
  };
}

// Isolated Book Nook easter egg: after the bottom candle is blown out,
// the female archer walks the last shelf, looses a flaming arrow, then leaves.
function NookArcher({ playing, wickRef, boardRef, onRelight, onDone }) {
  const stageRef = useRef(null);
  const onRelightRef = useRef(onRelight);
  const onDoneRef = useRef(onDone);
  const [phase, setPhase] = useState("wait");
  const [pose, setPose] = useState(() => measurePose(null, null, null));

  onRelightRef.current = onRelight;
  onDoneRef.current = onDone;

  useLayoutEffect(() => {
    if (!playing) return;
    setPose(
      measurePose(
        stageRef.current,
        boardRef && boardRef.current,
        wickRef && wickRef.current
      )
    );
  }, [playing, wickRef, boardRef]);

  useEffect(() => {
    if (!playing) {
      setPhase("wait");
      return undefined;
    }

    const timers = [];
    const later = (fn, ms) => {
      timers.push(setTimeout(fn, ms));
    };

    if (prefersReducedMotion()) {
      later(() => onRelightRef.current?.(), 480);
      later(() => onDoneRef.current?.(), 900);
      return () => timers.forEach(clearTimeout);
    }

    later(() => setPhase("walk"), 360);
    later(() => setPhase("plant"), 2680);
    later(() => setPhase("draw"), 3280);
    later(() => setPhase("shoot"), 4480);
    later(() => onRelightRef.current?.(), 4840);
    later(() => setPhase("recover"), 5020);
    later(() => setPhase("leave"), 5560);
    later(() => onDoneRef.current?.(), 7400);

    return () => timers.forEach(clearTimeout);
  }, [playing]);

  if (!playing) return null;

  const vars = {
    "--start-x": `${pose.startX}px`,
    "--stop-x": `${pose.stopX}px`,
    "--walk-y": `${pose.walkY}px`,
    "--bolt-x": `${pose.boltX}px`,
    "--bolt-y": `${pose.boltY}px`,
    "--wick-x": `${pose.wickX}px`,
    "--wick-y": `${pose.wickY}px`,
    "--fly-deg": `${pose.flyDeg}deg`,
    "--archer-w": `${pose.w}px`,
    "--archer-h": `${pose.h}px`,
    "--archer-src": `url(${ARCHER_SRC})`,
  };

  const showFigure = phase !== "wait";
  const drawn = phase === "draw" || phase === "shoot" || phase === "recover";
  const stepping = phase === "walk" || phase === "leave";

  return (
    <div className="nook-archer-stage" ref={stageRef} aria-hidden="true">
      {showFigure && (
        <div className={`nook-archer nook-archer--${phase}`} style={vars}>
          <span className="nook-archer-shadow" />
          <div className="nook-archer-figure">
            {drawn ? (
              <span className="nook-archer-full" />
            ) : (
              <>
                <span className="nook-archer-body" />
                <span className="nook-archer-leg nook-archer-leg--back" />
                <span className="nook-archer-leg nook-archer-leg--front" />
              </>
            )}
            <span className="nook-archer-sheen" />
            {phase === "draw" && (
              <div className="nook-archer-bolt nook-archer-bolt--draw">
                <span className="nook-archer-trail" />
                <span className="nook-archer-shaft" />
                <span className="nook-archer-head">
                  <span className="nook-archer-flame">
                    <span className="nook-archer-flame-core" />
                  </span>
                </span>
                <span className="nook-archer-ember nook-archer-ember--a" />
                <span className="nook-archer-ember nook-archer-ember--b" />
                <span className="nook-archer-ember nook-archer-ember--c" />
                <span className="nook-archer-ember nook-archer-ember--d" />
              </div>
            )}
          </div>
          {stepping && (
            <div className="nook-archer-dust">
              <span className="nook-archer-dust-puff nook-archer-dust-puff--a" />
              <span className="nook-archer-dust-puff nook-archer-dust-puff--b" />
            </div>
          )}
        </div>
      )}
      {(phase === "wait" || phase === "walk") && (
        <div className="nook-archer-plumes" style={vars}>
          <span className="nook-archer-plume nook-archer-plume--a" />
          <span className="nook-archer-plume nook-archer-plume--b" />
          <span className="nook-archer-plume nook-archer-plume--c" />
          <span className="nook-archer-plume nook-archer-plume--d" />
        </div>
      )}
      {phase === "shoot" && (
        <div className="nook-archer-bolt nook-archer-bolt--shoot" style={vars}>
          <span className="nook-archer-trail" />
          <span className="nook-archer-shaft" />
          <span className="nook-archer-head">
            <span className="nook-archer-flame">
              <span className="nook-archer-flame-core" />
            </span>
          </span>
          <span className="nook-archer-ember nook-archer-ember--a" />
          <span className="nook-archer-ember nook-archer-ember--b" />
          <span className="nook-archer-ember nook-archer-ember--c" />
          <span className="nook-archer-ember nook-archer-ember--d" />
        </div>
      )}
      {phase === "shoot" && (
        <>
          <span className="nook-archer-hit" style={vars} />
          <span className="nook-archer-hit nook-archer-hit--ring" style={vars} />
        </>
      )}
    </div>
  );
}

export default NookArcher;
